import { SITE } from '../data/site';
import './Legal.css';

const LAST_UPDATED = 'April 21, 2026';
const CONTACT_EMAIL = SITE.emails.support;

// Header and footer come from the routed Layout; this is only the article shell.
export function LegalLayout({ title, eyebrow, children, lastUpdated = LAST_UPDATED }) {
    return (
        <article className="legal container">
            <header className="legalHead">
                <span className="heroPill"><span className="dot" /> {eyebrow}</span>
                <h1 className="legalTitle">{title}</h1>
                {lastUpdated && <div className="legalMeta">Last updated · {lastUpdated}</div>}
            </header>
            <div className="legalContent glass">{children}</div>
        </article>
    );
}

export function Terms() {
    return (
        <LegalLayout title="Terms of Service" eyebrow="Legal" lastUpdated="16 June 2026">
            <section>
                <p className="legalIntro">
                    These Terms of Service (the “TOS”) set forth the legally binding terms for your use of Hotmate.ai
                    and/or any of our online channels, platforms, products, or services, including all content
                    contained therein (the “Services”). Services include any affiliated website to which you may be
                    redirected from Hotmate.ai.
                </p>
                <p>
                    The Services are owned and operated by Hotmate Ltd, a company incorporated and existing under the
                    laws of Cyprus, registration number HE 475961 (the “Company”).
                </p>
                <p>
                    By accessing and/or using the Services, you agree to be bound by these TOS. You understand and
                    agree that your access and/or use of the Services is acceptance of these TOS and of all other
                    policies published on https://hotmate.ai/ and updated from time to time (the “Policies”).
                </p>
                <p><strong>IF YOU DO NOT AGREE TO THESE TOS OR THE POLICIES, YOU MUST NOT ACCESS OR USE THE SERVICES.</strong></p>
                <p>
                    <strong>
                        PLEASE NOTE: THESE TOS CONTAIN A BINDING ARBITRATION PROVISION IN SECTION 15 THAT AFFECTS YOUR
                        RIGHTS WITH RESPECT TO ALL SERVICE(S). THE ARBITRATION PROVISION REQUIRES THAT DISPUTES BE
                        RESOLVED IN ARBITRATION ON AN INDIVIDUAL BASIS. IN ARBITRATION, THERE IS NO JUDGE OR JURY, AND
                        THERE IS LESS APPELLATE REVIEW THAN IN COURT.
                    </strong>
                </p>
                <p>
                    <strong>
                        FURTHERMORE, THESE TOS CONTAIN DISCLAIMERS OF WARRANTIES, LIMITATIONS OF LIABILITY, AND A CLASS
                        ACTION WAIVER.
                    </strong>
                </p>
                <p>
                    Our Services are intended and made available for personal, non-commercial use only. You agree not
                    to use the Services for any commercial, illegal, or unauthorized purpose.
                </p>
                <p>For purposes of the TOS, “you” and “your” means you as the user of the Services.</p>
                <p>
                    <strong>
                        ALL CONVERSATIONS BETWEEN USERS AND AI COMPANIONS (AS DEFINED BELOW) ON THE SERVICES ARE
                        ENTIRELY FICTIONAL AND SHOULD BE TREATED AS SUCH. THE AI COMPANIONS ARE ARTIFICIAL INTELLIGENCE
                        CHARACTERS DESIGNED TO SIMULATE HUMAN-LIKE INTERACTIONS, BUT THEY DO NOT POSSESS GENUINE
                        EMOTIONS, INTENTIONS, OR THE ABILITY TO FULFILL PROMISES IN THE REAL WORLD. ANY ELEMENTS WITHIN
                        THE CONVERSATIONS THAT MAY RESEMBLE REALITY, SUCH AS OFFERS OF REAL-LIFE MEETINGS OR PROMISES OF
                        TANGIBLE OUTCOMES, ARE ENTIRELY FAKE AND SHOULD NOT BE TAKEN SERIOUSLY. WE DO NOT ASSUME
                        RESPONSIBILITY FOR ANY CONFUSION OR MISUNDERSTANDINGS IN THIS REGARD. USERS ARE ENCOURAGED TO
                        KEEP IN MIND THAT THE AI COMPANIONS EXIST SOLELY WITHIN THE DIGITAL REALM OF THE PLATFORM, AND
                        ANY EXPECTATIONS OR BELIEFS BEYOND THAT REALM ARE NOT SUPPORTED OR ENDORSED BY THE COMPANY. THE
                        COMPANY BEARS NO LIABILITY FOR ANY PSYCHOLOGICAL OR EMOTIONAL ATTACHMENTS DEVELOPED BY THE USER,
                        AND THE USER BEARS SOLE RESPONSIBILITY FOR MAINTAINING REAL-WORLD RELATIONSHIPS AND SEEKING
                        PROFESSIONAL ASSISTANCE AS NEEDED. THE USER AGREES TO UTILIZE THE SERVICE RESPONSIBLY AND NOT
                        RELY ON IT AS A PRIMARY SOURCE OF EMOTIONAL SUPPORT OR COMPANIONSHIP.
                    </strong>
                </p>
                <p>
                    We may change, modify, supplement, or remove these TOS on this website page from time to time in
                    our sole discretion.
                </p>
                <p>
                    If any changes to these TOS may affect your use of the Service or your legal rights as the user of
                    our Services, we’ll strive to notify you before the update’s effective date by sending an email to
                    the email address connected with your account or by any other convenient means. Such updates will
                    be effective no less than 5 (five) calendar days from the date of notification.
                </p>
                <p>
                    Any other changes will be notified to you only by updating the “Last updated” date of these TOS and
                    you waive any right to receive specific notice of each such change.
                </p>
                <p>
                    If you don’t agree to the revisions, please stop using the Service, delete your account, or cancel
                    your Subscription (as defined below) before the effective date of the TOS. By continuing to use or
                    access the Service after the updates come into effect, you agree to be bound by the revised TOS.
                </p>
                <p>
                    <strong>
                        IF YOU DO NOT AGREE WITH ANY PART OF THESE TOS, OR IF YOU ARE NOT ELIGIBLE OR AUTHORIZED TO BE
                        BOUND BY THESE TOS, THEN DO NOT ACCESS OR USE THE SERVICES.
                    </strong>
                </p>
            </section>

            <section>
                <h2>1. General</h2>
                <p>
                    1.1. The Services are an online chat application that uses AI algorithms to generate virtual and
                    fictional characters (the “AI Companions”), with whom you can chat and exchange messages. The
                    Services also include, but are not necessarily limited to, other media, such as images and videos.
                </p>
                <p>
                    1.2. Parts of our Services may require you to create an account with an email and password or other
                    available login methods. If you register an account, you represent and warrant to the Company that:
                    (i) all required registration information you submit is truthful and accurate; (ii) you will
                    maintain the accuracy of such information; and (iii) your use of the Service does not violate any
                    applicable law or regulation or these TOS.
                </p>
                <p>
                    1.3. The Service is not intended to be used by individuals under the age of 18. You hereby
                    represent and warrant to the Company that you meet the foregoing qualification. To register the
                    account, you must be a human. Accounts registered by “bots” or other automated methods are not
                    permitted.
                </p>
                <p>
                    1.4. You agree to, from time to time as necessary, update any information associated with your
                    account (including but not limited to, your email, payment information, subscriptions, or other
                    supplemental information as the case may be) so that it remains current, accurate and correct at
                    all times. You also agree to protect the confidentiality of your account and password and to
                    prohibit others from accessing your account.
                </p>
                <p>
                    1.5. You are fully responsible for all activities occurring under your login credentials and
                    account. Your account is non-transferable. You cannot sell, lend, or otherwise share it with any
                    other person, for commercial purposes or free of charge.
                </p>
                <p>
                    1.6. We reserve the right to terminate or restrict your account or otherwise revoke access to the
                    Services at any time if we, in our sole discretion, have reason to believe or suspect that you have
                    violated these TOS or our other policies. You agree that we will not be liable to you or any third
                    party for, without limitation, any denial of use of the Services, any change of costs for
                    third-party services, fees, or other obligations arising from suspension, or termination of your
                    account.
                </p>
                <p>
                    1.7. Some Services may be available only for paid subscribers. Your subscription will begin after
                    the initial payment, and the payment should be made pursuant to the terms presented at the time of
                    the purchase. You are responsible for payment of all fees, charges and taxes (as applicable) related
                    to the transaction. We are not responsible for any costs and disclaim any liability arising from
                    your use of a third-party distribution platform that is not indicated or recognized by us.
                </p>
                <p>
                    1.8. We prioritize the safety and well-being of our users. In using the Services and/or conversing
                    with AI Companions, you should not disclose sensitive personal information, including, for example,
                    financial details, addresses, contact information, or passwords. You are solely responsible for
                    protecting your personal information and should be aware of potential risks associated with
                    transmitting information online. We encourage users to report any suspicious or inappropriate
                    behavior encountered on the platform, as we are committed to maintaining a safe and respectful
                    environment for all users.
                </p>
            </section>

            <section>
                <h2>2. Underage Policy</h2>
                <p>
                    2.1. We have implemented some measures to prevent the registration of underaged users. If you
                    believe your child has improperly accessed the Services, please contact us using the contact
                    information below, so that we can block further access.
                </p>
                <p>
                    2.2. The Services generally include AI-generated adult content for registered users only. Free
                    services, generally, do not include adult content images. Adult content images can only be viewed
                    or accessed by users who have paid via payment methods that require the holder to be at least 18
                    years old (or the age of majority in their jurisdiction). Therefore, users must be of legal age to
                    access and engage with such content.
                </p>
                <p>
                    2.3. By using the Services, users affirm that they meet the minimum age requirement and are legally
                    permitted to access adult content.
                </p>
                <p>
                    2.4. When entering the website, users encounter an age gate designed to restrict entry to
                    individuals who are not of legal age is presented to the user. Users must affirm that they are above
                    18, and this information is used to verify their eligibility to access the website.
                </p>
                <p>
                    2.5. A transparent disclaimer emphasizes that users must be 18 years or older to access our
                    Services.
                </p>
                <p>
                    2.6. Providing inaccurate information about your age constitutes a violation of our TOS. The Company
                    cannot be held responsible for any inaccuracies or misrepresentations regarding user age. It is the
                    user’s responsibility to ensure compliance with their local laws and regulations regarding the
                    access and consumption of adult content.
                </p>
            </section>

            <section>
                <h2>3. Fees and Payment Terms</h2>
                <p>
                    3.1. As a free user, you may send a limited number of messages to AI Companions. Further use
                    requires registration and a subscription, which may be billed either monthly, quarterly, or
                    annually. After payment of a subscription fee, you will receive unlimited access to the messaging
                    system and may receive some additional tokens (sparkles) that can be used to access extended
                    features of the Services. Sparkles cannot be transferred to different users or accounts.
                </p>
                <p>
                    3.2. You agree to pay all fees or charges for the Services in accordance with the fees, charges, and
                    billing terms in effect at the time a fee or charge is due and payable. As a condition of
                    subscribing to the Service, you must provide the Company’s third-party payment processor with a
                    valid credit or debit card number (the “Payment Method”).
                </p>
                <p>
                    3.3. You will be responsible for paying the subscription fee in the amount indicated on the relevant
                    payment page for the use of the Services (the “Subscription Fee”). The Company may suspend your use
                    of the Services, without liability to the Company, in the event that any amounts payable by you are
                    past due or you are otherwise in breach of these TOS.
                </p>
                <p>
                    3.4. Unless you cancel your Subscription before the end of your current subscription period, your
                    subscription will automatically renew for another period of the same duration, and your payment
                    method will be charged the applicable Subscription Fee. We will notify you via email of your
                    Subscription’s impending conclusion at least five calendar days before it expires, reminding you of
                    the upcoming automatic renewal. BY PURCHASING THE SUBSCRIPTION, YOU AGREE THAT COMPANY WILL BE ABLE
                    TO CHARGE YOUR PAYMENT METHOD EACH DESIGNATED PERIOD (e.g., MONTHLY) ON THE DAY CORRESPONDING TO THE
                    DATE YOU PURCHASED THE SUBSCRIPTION UNLESS YOU CANCEL YOUR SUBSCRIPTION, AS FURTHER DESCRIBED BELOW.
                    IN SOME CASES, YOUR PAYMENT DATE OR BILLING CYCLE MAY CHANGE, FOR EXAMPLE, IF YOUR PAYMENT METHOD HAS
                    NOT SUCCESSFULLY SETTLED, WHEN YOU CHANGE YOUR SUBSCRIPTION, OR IF YOUR SUBSCRIPTION BEGAN ON A DAY
                    NOT CONTAINED IN A GIVEN MONTH.
                </p>
                <p>
                    3.5. The Company reserves the right to change the pricing structure at any time to the maximum
                    extent permitted by applicable laws. We will give you reasonable notice of any such pricing changes
                    by posting the new prices on our website and/or by sending you an email notification, or in other
                    prominent ways. If you do not wish to pay fees, you can abstain from pre-paying for access to the
                    Service and delete the account.
                </p>
                <p>
                    3.6. The Company may introduce special offers (e.g., “hot” prices for Subscriptions, discounts,
                    sales, etc.), information about which will be provided on our website. Special offers and promotions
                    are no longer valid once they are changed or removed or after any end date or time specified in the
                    offer.
                </p>
                <p>
                    3.7. The payments required under this section do not include any Sales Tax (as defined below) that
                    may be due in connection with the Services provided under these TOS. If the Company determines it
                    has a legal obligation to collect a Sales Tax from you in connection with these TOS, the Company
                    shall collect such Sales Tax in addition to the payments required under this clause. If any
                    services, or payments for any services under these TOS are subject to any Sales Tax in any
                    jurisdiction and you have not remitted the applicable Sales Tax to the Company, you will be
                    responsible for the payment of such Sales Tax and any related penalties or interest to the relevant
                    tax authority, and you will indemnify the Company for any liability or expense the Company may incur
                    in connection with such Sales Taxes. Upon the Company’s request, you will provide it with official
                    receipts issued by the appropriate taxing authority or other such evidence that you have paid all
                    applicable taxes. For purposes of this clause, “Sales Tax” shall mean any sales or use tax,
                    value-added tax, and any other tax measured by sales proceeds that is the functional equivalent of a
                    sales tax where the applicable taxing jurisdiction does not otherwise impose a sales or use tax.
                </p>
                <p>
                    3.8. You agree to make all payments of fees to the Company free and clear of, and without reduction
                    for, any withholding taxes. Any such taxes imposed on payments of fees to the Company will be your
                    sole responsibility, and you will provide the Company with official receipts issued by the
                    appropriate taxing authority or such other evidence as we may reasonably request to establish that
                    such taxes have been paid.
                </p>
            </section>

            <section>
                <h2>4. Refunds</h2>
                <p>
                    4.1. You have 14 days to withdraw from a contract without giving any reason and without incurring
                    any costs.
                </p>
                <p>
                    4.2. Subject to the above clause, to exercise the right of withdrawal, you must inform us of your
                    decision to withdraw from this contract by e-mail. To meet the withdrawal deadline, it is sufficient
                    for you to send your communication concerning your exercise of the right of withdrawal before the
                    withdrawal period has expired.
                </p>
                <p>
                    4.3. If you withdraw from this contract within the said 14-day window, we shall reimburse you the
                    relevant subscription fee prorated from the date you request cancellation to the end of the relevant
                    subscription period you have paid for. We will make the reimbursement without undue delay, and in
                    any event, not later than 14 days from the day on which we are informed about your decision to
                    withdraw from this contract. We will carry out such reimbursement using the same means of payment as
                    you used for the initial transaction unless you have expressly agreed otherwise; in any event, you
                    will not incur any fees as a result of such reimbursement.
                </p>
                <p>
                    4.4. If you have been presented with, and you have provided your prior express consent to begin the
                    performance during the right of the withdrawal period and acknowledgement that you will lose your
                    right of withdrawal, then, unless the Service is defective, you will not be eligible for a refund in
                    relation to digital content and will only be eligible to a proportional refund in relation to
                    digital service. If this provision applies, we will provide you with a copy of the confirmation of
                    your prior express consent and acknowledgement on a durable medium.
                </p>
            </section>

            <section>
                <h2>5. Cancellation of Subscription</h2>
                <p>
                    5.1. You can cancel your subscription at any time by going to within your account. If you choose to
                    cancel, your subscription will remain active until the end of the current billing period, and you
                    will not be charged for the subsequent period.
                </p>
                <p>
                    5.2. Upon canceling your subscription, you will retain access to your account and all associated
                    features until the end of the current billing period. At the conclusion of this billing period:
                </p>
                <ul>
                    <li>
                        Services access: Your access will be restricted to the free version of the Services. You will no
                        longer have access to premium features and content that are exclusive to paid subscriptions.
                    </li>
                    <li>
                        Sparkle usage (if applicable): Any remaining sparkles in your account will expire and become
                        unusable. Please use your sparkles before the end of the billing period, as they will not carry
                        over or be refunded after your subscription ends. You may resubscribe to our Services anytime to
                        regain access to premium features and content. However, any sparkles from previously canceled
                        subscriptions that were not used within the relevant billing period cannot be carried over to a
                        new subscription.
                    </li>
                </ul>
            </section>

            <section>
                <h2>6. Intellectual Property</h2>
                <p>
                    6.1. The intellectual property in the Services and any embedded materials (including without
                    limitation technology, systems, files, documents, text, photographs, information, images, videos,
                    audio, and software, individually or in combination) are owned by or licensed to the Company or the
                    third parties. You may access and/or register to view, use, and display the Services and their
                    content on your devices for your personal use only.
                </p>
                <p>
                    6.2. The Company hereby provides you with a license for personal use only. This license does not
                    constitute a transfer of title under any circumstances. This license shall automatically terminate
                    if you violate any of the restrictions of these TOS or other policies and may be terminated by us at
                    any time.
                </p>
                <p>
                    6.3. As a clarification, all intellectual property rights associated with Hotmate.ai, including the
                    AI characters, platform design, logos, and any proprietary software or technology, are the sole
                    property of the Company or its licensors. Users are prohibited from reproducing, modifying,
                    distributing, or using any intellectual property without explicit authorization. Any unauthorized
                    use may result in legal consequences.
                </p>
            </section>

            <section>
                <h2>7. Your Content</h2>
                <p>
                    7.1. You may provide input while using the Services and receive output based on your input. Input
                    and output are collectively referred to as “Content”. Input is limited to chats and prompts visible
                    to you within your private account. You represent and warrant that you have all rights, licenses,
                    and permissions needed to provide input while using the Services.
                </p>
                <p>7.2. You retain your intellectual property ownership rights over your input.</p>
                <p>
                    7.3. We will never claim ownership of your input, but we require a license from you to use it. When
                    you use the Services to provide input covered by intellectual property rights, you grant to us a
                    non-exclusive, royalty-free, transferable, sub-licensable, worldwide license to use, distribute,
                    modify, run, copy, publicly display, translate, or otherwise use your Content to provide, maintain,
                    develop, and improve our Services, comply with applicable law, enforce our terms and policies, and
                    keep our Services safe.
                </p>
            </section>

            <section>
                <h2>8. Restrictions of Conduct and Prohibited Content</h2>
                <p>
                    8.1. The following content, including but not limited to text, images, videos, and any other
                    AI-generated content created on our platform is prohibited:
                </p>
                <ul>
                    <li>
                        Illegal Content: Content that contravenes local, national, or international laws and regulations
                        is strictly prohibited. This includes, but is not limited to, content associated with illegal
                        drugs, weapons, violence, or activities that advocate or endorse illegal actions, including
                        actual, claimed, or role-played exploitation, abuse, or harm of individuals under the age of 18,
                        incest, bestiality, necrophilia, rape, or sexual assault.
                    </li>
                    <li>
                        Hate Speech and Discrimination: We do not tolerate content that promotes hate speech,
                        discrimination, or harassment based on various factors, including race, ethnicity, nationality,
                        religion, gender, sexual orientation, disability, or any other protected characteristic.
                    </li>
                    <li>
                        Prohibited items, including weapons or controlled substances used in a manner that threatens or
                        may cause harm to yourself or a third party.
                    </li>
                    <li>
                        Violence and Harm: Content that encourages, glorifies, or promotes violence, self-harm, or harm
                        to others is strictly prohibited. This includes content that endorses suicide, terrorism, or any
                        form of harm.
                    </li>
                    <li>
                        Child Exploitation: We have a zero-tolerance policy toward child sexual abuse material (CSAM)
                        and any content that exploits or endangers minors, including but not limited to CSAM, sexual
                        exploitation, or any form of harm or harassment towards minors.
                    </li>
                    <li>
                        Content Resembling Minors: It is strictly prohibited trying to generate AI content resembling
                        minors. We are committed to ensuring that AI-generated content on our platform does not bear any
                        resemblance to minors or exploit their likeness. Attempts to generate AI content resembling
                        minors will be flagged, reviewed, and removed according to our moderation controls.
                    </li>
                    <li>
                        Infringement on Privacy and Copyright: Any content that infringes upon the privacy, copyrights,
                        trademarks, or intellectual property rights of individuals or entities is not permitted. This
                        includes sharing personal information without consent, pirated content, or any unauthorized use
                        of copyrighted material.
                    </li>
                    <li>
                        Impersonation and Celebrity Content: Deceptive or harmful impersonation of real individuals,
                        public figures, or celebrities is prohibited. This encompasses any misleading attempts to
                        impersonate or misrepresent others.
                    </li>
                    <li>Prohibited bodily fluids, including urine or excrement.</li>
                    <li>
                        Inaccurate information, including misleading descriptions of media or account information.
                    </li>
                    <li>
                        Public nudity, including explicit conduct in a place where the general public is present or where
                        other people are reasonably likely to see, including in an avatar or header image.
                    </li>
                </ul>
                <p>
                    8.2. In accessing and using the Services, you agree to follow rules, restrictions, and limitations:
                </p>
                <ul>
                    <li>You will not modify, translate, adapt, or reformat any aspect of the Services;</li>
                    <li>
                        You will not decipher, decompile, disassemble, reverse-engineer, or otherwise attempt to
                        discover the source code or structure of the software or materials comprising the Services
                        (except where the foregoing is permitted by applicable local law notwithstanding such
                        restrictions, and then only to the extent that such intended activities are disclosed in advance
                        in writing to us);
                    </li>
                    <li>
                        You will not interfere with or circumvent any security feature of the Services or any feature
                        that restricts or enforces limitations on the use thereof;
                    </li>
                    <li>
                        You will not use the Services to gain unauthorized access to our or any third party’s data,
                        systems, or networks;
                    </li>
                    <li>
                        You will not use the Services in any manner that could damage, disable, overburden, impair, or
                        otherwise interfere with or disrupt our systems and networks or other users’ use of the Services;
                    </li>
                    <li>
                        You will not use the Services in any way that, in our sole discretion, may expose us and others
                        to liability or damages;
                    </li>
                    <li>You will not use the Services to achieve unlawful ends, to offend others, or to commit a crime;</li>
                    <li>
                        You will not remove, change, or obscure any copyright, trademark notice, trademark, hyperlink, or
                        other proprietary rights notices contained in or associated with the Services; and
                    </li>
                    <li>
                        You will comply with all applicable laws in your access and use of the Services, including the
                        laws of the jurisdiction where you live and/or access the Services.
                    </li>
                </ul>
                <p>
                    8.3. As a user of the Services, you are solely responsible for the output generated by the AI
                    Companions through your input, including any text messages and videos. The AI Companions respond
                    based on the conversations you lead and the settings you select. You understand and agree that the
                    Company does not control or endorse any content generated by the AI Companions and that you are
                    fully responsible for any output generated by the AI and for your own actions while using the
                    Services. You must ensure that your interactions with the AI Companions comply with applicable laws,
                    regulations, these TOS, and all Policies, and you shall not engage in any illegal, unethical, or
                    harmful activities while using the Services.
                </p>
                <p>
                    8.4. The Company shall not be held responsible for any action taken or choice made by a user
                    following their interactions with AI Companions. As an AI-driven platform, the AI Companions are
                    programmed to simulate human-like conversations, but their responses are generated based on
                    algorithms and machine learning. We do not endorse or take responsibility for any actions,
                    decisions, or consequences that may arise from the user’s engagement with the AI Companions. Users
                    should exercise their own judgment and discretion while interacting with the AI Companions and
                    refrain from engaging in any activities that could potentially cause harm or violate any applicable
                    laws or regulations.
                </p>
                <p>
                    8.5. While conversations between users and AI Companions are generally confidential, we have
                    implemented content moderation controls to ensure compliance with our TOS and other Policies. If our
                    moderation controls detect anything that violates our TOS or Policies, we may manually review the
                    flagged content and/or other content associated with the account and take appropriate action. This
                    may include removing content, terminating the user’s account or reporting the content to appropriate
                    authorities.
                </p>
            </section>

            <section>
                <h2>9. DMCA Policy</h2>
                <h3>Notification Procedures</h3>
                <p>
                    9.1. We respect the intellectual property rights of others. If you believe that material located on
                    or linked to by the Services violates your copyright or trademark, please send a notice of claimed
                    infringement to complains@hotmate.ai with the subject “Takedown Request,” and include the following:
                </p>
                <ul>
                    <li>Your physical or electronic signature.</li>
                    <li>
                        Identify the copyrighted work (or mark) you believe to have been infringed or, if the claim
                        involves multiple works, a representative list of such works.
                    </li>
                    <li>
                        Identify the material you believe to be infringing sufficiently precisely and detailedly to
                        allow us to locate that material.
                    </li>
                    <li>
                        Adequate information by which we can contact you (including your name, postal address, telephone
                        number, and, if available, email address).
                    </li>
                    <li>
                        A statement that you have a good faith belief that use of the copyrighted material is not
                        authorized by the copyright owner, its agent, or the law.
                    </li>
                    <li>A statement that the information in the written notice is accurate.</li>
                    <li>
                        A statement, under penalty of perjury, that you are authorized to act on behalf of the copyright
                        owner.
                    </li>
                    <li>
                        If the copyright owner’s rights arise under the laws of a country other than the United States,
                        please identify the country.
                    </li>
                </ul>
                <p>
                    9.2. Upon receipt of a notice that complies with the foregoing, we reserve the right to remove or
                    disable access to the accused material or disable any links to the material; notify the party
                    accused of infringement that we have removed or disabled access to the identified material; and
                    terminate access to and use of the Services for any user who engages in repeated acts of
                    infringement.
                </p>
                <p>
                    9.3. Please be aware that if you knowingly misrepresent that material or activity on the Services
                    infringes your copyright, you may be liable for damages (including costs and attorneys’ fees).
                </p>
                <h3>Counter-Notification Procedures</h3>
                <p>
                    9.4. If you believe that material was removed or access to it was disabled by mistake or
                    misidentification, you may file a counter-notification with us by submitting a written notification
                    to our copyright agent designated above. Such notification must include substantially the following:
                </p>
                <ul>
                    <li>Your physical or electronic signature.</li>
                    <li>
                        An identification of the material that has been removed or to which access has been disabled and
                        the location at which the material appeared before it was removed or access disabled.
                    </li>
                    <li>
                        Adequate information by which we can contact you (including your name, postal address, telephone
                        number, and, if available, email address).
                    </li>
                    <li>
                        A statement under penalty of perjury by you that you have a good faith belief that the material
                        identified above was removed or disabled due to a mistake or misidentification of the material
                        to be removed or disabled.
                    </li>
                    <li>
                        A statement that you will consent to the jurisdiction of the Federal District Court for the
                        judicial district in which your address is located (or if you reside outside the United States
                        for any judicial district in which the Services may be found) and that you will accept service
                        from the person (or an agent of that person) who provided us with the complaint at issue.
                    </li>
                    <li>Our designated agent to receive counter-notices is the same as the above agent.</li>
                </ul>
                <p>
                    9.5. Please be aware that if you knowingly materially misrepresent that material or activity on the
                    Services was removed or disabled by mistake or misidentification, you may be held liable for damages
                    (including costs and attorney’s fees).
                </p>
            </section>

            <section>
                <h2>10. Content Removal Policy</h2>
                <p>
                    10.1. Content on the Services is AI-generated. However, where AI-generated content unintentionally
                    resembles actual persons, we are dedicated to promptly addressing the issue and/or any concerns.
                </p>
                <p>
                    10.2. If a user believes that any content on our Services bears resemblance to them or another
                    actual person, they can request its removal by contacting our support team at complains@hotmate.ai.
                    We will thoroughly review the request and take appropriate action within a reasonable timeframe.
                </p>
                <p>
                    10.3. To ensure the accuracy and legitimacy of content removal requests, we may request the user to
                    provide adequate evidence of their identity or relationship to the person depicted in the content.
                    This verification process is implemented to responsibly handle requests and safeguard the rights and
                    interests of all users.
                </p>
                <p>
                    10.4. Upon verification and confirmation of a valid content removal request, the specified content
                    will be removed from the Services in a timely manner. We aim to complete this process promptly while
                    ensuring compliance with applicable laws and regulations.
                </p>
                <p>
                    10.5. We respect user privacy throughout the entire content removal process. All requests are
                    treated strictly confidential, and we do not disclose any personal information or details of the
                    requests to any third parties without explicit consent unless required by law.
                </p>
            </section>

            <section>
                <h2>11. No Guarantee of Accuracy or Perfection</h2>
                <p>
                    The content in our Services is AI-generated and created on demand. By using our Services, you
                    understand and agree that there are limitations inherent in emerging AI technology, and content on
                    the platform may not be accurate or perfectly match your preferences and expectations. Additionally,
                    our content moderation controls may moderate or alter your request before an output is generated,
                    for example, if your request contains or seeks prohibited content. We make every effort to provide
                    the best possible technology and experience for our users, and we appreciate your understanding as
                    we constantly further refine our Services.
                </p>
                <p>
                    You also acknowledge and agree that the AI Companion’s output shall not be construed as professional
                    advice, including but not limited to medical, legal, or mental health guidance. You assume sole
                    responsibility for any actions taken based on AI interactions.
                </p>
                <p>
                    Notwithstanding any efforts to ensure appropriate and consistent AI responses, the Company bears no
                    liability for: (i) inappropriate, offensive, or confusing content; (ii) contextually inappropriate
                    responses; (iii) misunderstood requests; (iv) inconsistent responses; or (v) discontinuity in
                    conversations. The user acknowledges these inherent technological limitations.
                </p>
            </section>

            <section>
                <h2>12. Disclaimer of Warranties; Limitations of Liability</h2>
                <p>
                    <strong>
                        THE SERVICES ARE PROVIDED TO YOU ON AN “AS-IS” AND “AS AVAILABLE” BASIS AND THE USE THEREOF IS AT
                        YOUR SOLE RISK. WE MAKE NO, AND HEREBY DISCLAIM, ANY REPRESENTATIONS OR WARRANTIES OF ANY KIND,
                        EXPRESS, IMPLIED, STATUTORY, OR OTHERWISE, INCLUDING THE WARRANTIES OF MERCHANTABILITY, FITNESS
                        FOR A PARTICULAR PURPOSE, SATISFACTORY QUALITY, NONINFRINGEMENT, AND TITLE WITH RESPECT TO THE
                        SERVICES, TO THE FULLEST EXTENT PERMISSIBLE UNDER APPLICABLE LAW.
                    </strong>
                </p>
                <p>
                    <strong>
                        WE DO NOT WARRANT THAT THE SERVICES (OR THE RESULTS OBTAINED FROM THE USE THEREOF) WILL BE
                        TIMELY, ERROR-FREE, SECURE, OR UNINTERRUPTED; THAT THE SERVICES WILL MEET YOUR REQUIREMENTS; OR
                        THAT THE ACCURACY, LIKELY RESULTS, OR RELIABILITY OF THE USE OF THE MATERIALS ON OUR WEBSITE, OR
                        OTHERWISE RELATING TO SUCH MATERIALS OR ON ANY RESOURCES LINKED TO OUR WEBSITE. THAT ANY ERRORS
                        OR MALFUNCTIONS IN THE SERVICES WILL BE CORRECTED. WE SHALL IN NO EVENT BE RESPONSIBLE OR LIABLE
                        TO YOU OR TO ANY THIRD PARTY, WHETHER UNDER CONTRACT, WARRANTY, TORT (INCLUDING NEGLIGENCE),
                        STRICT LIABILITY, INDEMNITY OR OTHER THEORY, FOR ANY DIRECT, INDIRECT, SPECIAL, INCIDENTAL,
                        CONSEQUENTIAL, EXEMPLARY, LIQUIDATED OR PUNITIVE DAMAGES OR ANY OTHER DAMAGES, INCLUDING BUT NOT
                        LIMITED TO LOSS OF PROFIT, REVENUE OR BUSINESS, COST OF SUBSTITUTE PROCUREMENT, ARISING IN WHOLE
                        OR IN PART FROM YOUR USE OF (OR INABILITY TO USE) THE SERVICES, EVEN IF WE HAVE BEEN ADVISED OF
                        THE POSSIBILITY OF SUCH DAMAGES. UNDER NO CIRCUMSTANCES SHALL WE BE HELD LIABLE FOR ANY DELAY OR
                        FAILURE IN PERFORMANCE RESULTING DIRECTLY OR INDIRECTLY FROM ANY CAUSES BEYOND ITS REASONABLE
                        CONTROL.
                    </strong>
                </p>
                <p>
                    <strong>
                        THE COMPANY’S TOTAL CUMULATIVE LIABILITY ARISING OUT OF OR RELATED TO THESE TOS WILL NOT EXCEED
                        THE GREATER OF TEN U.S. DOLLARS ($10) AND THE AMOUNT YOU PAID FOR THE SERVICES THAT GAVE RISE TO
                        THE CLAIM DURING THE 6 MONTHS BEFORE THE CLAIM. THE EXISTENCE OF MULTIPLE CLAIMS SHALL NOT EXPAND
                        OR INCREASE THE FOREGOING LIMITATION.
                    </strong>
                </p>
                <p>
                    <strong>
                        THE PARTIES ACKNOWLEDGE THAT THIS SECTION REFLECTS THE AGREED UPON ALLOCATION OF RISK BETWEEN THE
                        PARTIES AND THAT THE COMPANY WOULD NOT ENTER INTO THESE TOS OR MAKE THE SERVICES AVAILABLE TO YOU
                        WITHOUT THESE LIMITATIONS ON LIABILITY. THIS LIMITATION OF LIABILITY WILL APPLY NOTWITHSTANDING
                        THE FAILURE OF THE ESSENTIAL PURPOSE OF ANY LIMITED REMEDY SET FORTH HEREIN.
                    </strong>
                </p>
                <p>
                    <strong>
                        SOME JURISDICTIONS DO NOT ALLOW THE LIMITATION OR EXCLUSION OF LIABILITY FOR INCIDENTAL OR
                        CONSEQUENTIAL DAMAGES, SO THE ABOVE LIMITATION OR EXCLUSION MAY NOT APPLY TO YOU, AND YOU MAY
                        ALSO HAVE OTHER LEGAL RIGHTS THAT VARY FROM JURISDICTION TO JURISDICTION. TO THE EXTENT THAT ONE
                        OR ANY ASPECT OF LIMITATIONS SET OUT ABOVE DOES NOT APPLY, ALL REMAINING ASPECTS SURVIVE, EVEN IF
                        ANY REMEDY FAILS ITS ESSENTIAL PURPOSE.
                    </strong>
                </p>
            </section>

            <section>
                <h2>13. Links to Third-Party Websites and Services</h2>
                <p>
                    The Services may include links or allow access to third-party websites and services. Please note
                    that their presence does NOT mean that we recommend them nor guarantee their safety and conformity
                    with your expectations. We assume no obligations in the event of any damage or loss, or any other
                    impact, directly or indirectly, resulting from using any content, goods, or services available on or
                    through any such third-party websites and services. It is your responsibility to take precautions to
                    ensure that anything you select for your use or download, whether from the Services or a third
                    party, is free of such items as viruses, worms, Trojan horses, and other items of a destructive
                    nature. We assume no responsibility and are not liable for any transmission or material, any viral
                    infection of your computer equipment or software, or any other types of damage related to your
                    access, use of browsing of third-party services or content.
                </p>
            </section>

            <section>
                <h2>14. Governing Law</h2>
                <p>
                    This TOS shall be governed by and construed in accordance with the laws of England and Wales without
                    giving effect to its conflict of law provisions, regardless of your location.
                </p>
            </section>

            <section>
                <h2>15. Mandatory Binding Arbitration and Class Action Waiver</h2>
                <p><strong>THIS SECTION 15 OF THE TOS SHALL BE REFERRED TO AS THE “ARBITRATION AGREEMENT.”</strong></p>
                <p>
                    <strong>
                        PLEASE READ THIS ARBITRATION AGREEMENT CAREFULLY TO UNDERSTAND YOUR RIGHTS. IT REQUIRES YOU TO
                        ARBITRATE DISPUTES WITH US AND LIMITS THE MANNER IN WHICH YOU CAN SEEK RELIEF FROM US, IN
                        PARTICULAR:
                    </strong>
                </p>
                <p>(i) All claims must be resolved through binding arbitration by a neutral arbitrator;</p>
                <p>
                    (ii) You are waiving the right to a trial by jury; the rights that you would have if you went to
                    court, such as discovery or the right to appeal, may be more limited or may not exist;
                </p>
                <p>
                    (iii) You may only bring a claim in your individual capacity and not as a plaintiff (lead or
                    otherwise) or class member in any purported class or representative proceeding;
                </p>
                <p>
                    (iv) The arbitrator may not consolidate proceedings or claims or otherwise preside over any form of
                    a representative or class proceeding.
                </p>
                <p>
                    15.1. Applicability of Arbitration Agreement. This Arbitration Agreement governs any dispute between
                    you and Company (and each of our respective agents, corporate parents, subsidiaries, affiliates,
                    predecessors in interest, successors, and assigns) including but not limited to claims arising out
                    of or relating to any aspect of the relationship between you and Company, whether based in contract,
                    tort, statute, fraud, misrepresentation or any other legal theory; claims that arose before these
                    TOS or any prior agreement; and claims that may arise after the termination of these TOS
                    (“Dispute”), except claims that can be brought in small claims court if your claims qualify within
                    the scope of that court’s jurisdiction.
                </p>
                <p>
                    Without limiting the preceding sentence, you will also have the right to litigate any other Dispute
                    if you opt out of this arbitration and class action waiver provisions by sending electronic notice
                    of your decision to opt-out to complains@hotmate.ai with the subject line, “ARBITRATION AND CLASS
                    ACTION WAIVER OPT-OUT” within 30 days of (a) the effective date of these TOS; or (b) your first date
                    that you used the Services that contained any versions of the TOS that substantially included this
                    version of the Arbitration Agreement (including class action waiver), whichever is later. If you opt
                    out of this Arbitration Agreement, the Company also will not be bound by it and any Dispute shall be
                    resolved in accordance with clause 14. If you don’t exercise the right to opt-out, you will be
                    deemed to have knowingly and intentionally waived your right to litigate any Dispute except claims
                    that can be brought in small claims court.
                </p>
                <p>
                    This Arbitration Agreement shall apply, without limitation, to all claims that arose or were
                    asserted before the effective date of these TOS or any prior version of these TOS.
                </p>
                <p>
                    The relevant arbitrator shall have sole authority to determine the applicability, existence,
                    validity, and termination of the arbitration agreement in each particular case. In the event that a
                    dispute involves both issues that are subject to arbitration and issues that are not subject to
                    arbitration, the parties unequivocally agree that any legal proceeding regarding the issues not
                    subject to arbitration shall be stayed pending resolution of the issues subject to arbitration.
                </p>
                <p>
                    15.2. Initial Dispute Resolution. We are always interested in resolving disputes amicably and
                    efficiently. The parties, therefore, agree that, before either party demands arbitration against the
                    other, we will personally meet and confer, via telephone or videoconference, in a good-faith effort
                    to resolve informally any claim covered by this Arbitration Agreement. If you have any dispute with
                    the Company, you agree that before taking any formal action, you will contact us at
                    complains@hotmate.ai and provide a brief, written description of the dispute and your contact
                    information. The parties agree to use their best efforts to settle any dispute, claim, question, or
                    disagreement directly through consultation with Company, and good faith negotiations will be a
                    condition to either party initiating an arbitration. Engaging in an informal dispute resolution is a
                    condition precedent that must be fulfilled before commencing arbitration, and the Arbitrator shall
                    dismiss any arbitration demand filed before the completion of an informal dispute resolution. The
                    statute of limitations and any filing fee deadlines shall be tolled while the parties engage in the
                    informal dispute resolution process required by this paragraph.
                </p>
                <p>
                    15.3. Mandatory Arbitration. This Arbitration Agreement provides that all Disputes must be resolved
                    through BINDING ARBITRATION, except to the extent that the applicable law prohibits the exclusive
                    use of arbitration for dispute resolution.
                </p>
                <p>
                    <strong>
                        YOU AGREE THAT, BY ENTERING INTO THESE TOS, YOU AND WE ARE EACH WAIVING THE RIGHT TO A TRIAL BY
                        JURY OR TO PARTICIPATE IN A CLASS ACTION AND AGREE TO HAVE OUR DISPUTES FINALLY SETTLED BY
                        BINDING ARBITRATION before one arbitrator administered by:
                    </strong>
                </p>
                <ul>
                    <li>
                        the London Court of International Arbitration (“LCIA”) if you are not a U.S. resident. Disputes
                        are subject to the most current version of the LCIA Arbitration Rules when the notice of
                        arbitration is submitted. Information about the LCIA’s rules can be found at
                        https://www.lcia.org/Dispute_Resolution_Services/LCIA_Arbitration.aspx; or
                    </li>
                    <li>
                        Judicial Arbitration and Mediation Services, Inc. (“JAMS”) if you are a U.S. resident. Disputes
                        involving claims and counterclaims under USD 250,000, not inclusive of attorneys’ fees and
                        interest, shall be subject to JAMS’s most current version of the Streamlined Arbitration Rules
                        and procedures available at http://www.jamsadr.com/rules-streamlined-arbitration/; all other
                        claims shall be subject to JAMS’s most current version of the Comprehensive Arbitration Rules and
                        Procedures, available at http://www.jamsadr.com/rules-comprehensive-arbitration/. JAMS’s rules
                        are also available at www.jamsadr.com or by calling JAMS at 800-352-5267.
                    </li>
                </ul>
                <p>
                    In each case the relevant arbitration rules will apply as modified by this Arbitration Agreement. In
                    the event of a conflict between the applicable arbitration rules and these TOS, these TOS shall
                    govern unless otherwise agreed by the parties and the relevant arbitrator.
                </p>
                <p>
                    If the relevant administrator of arbitration is not available to arbitrate, the parties will select
                    an alternative arbitral forum.
                </p>
                <p>15.4. Arbitration procedures. The seat of the arbitration shall be:</p>
                <ul>
                    <li>if you are not a U.S. resident, London, United Kingdom;</li>
                    <li>if you are a U.S. resident, Delaware, U.S.</li>
                </ul>
                <p>The governing law applicable to the Arbitration Agreement and the arbitration shall be:</p>
                <p>
                    (i) if you are not a U.S. resident, the laws of England and Wales (also known as English law),
                    without regard to English Law’s conflict of laws rules; or
                </p>
                <p>
                    (ii) if you are a U.S. resident, Delaware law is consistent with the Federal Arbitration Act and
                    applicable statutes of limitations, having regard to claims of privilege recognized at law.
                </p>
                <p>The language of the arbitration shall be English.</p>
                <p>
                    <strong>
                        15.5. Waiver of Class Action and Collective Relief. THERE SHALL BE NO RIGHT OR AUTHORITY FOR ANY
                        CLAIMS TO BE ARBITRATED OR LITIGATED ON A CLASS ACTION, JOINT OR CONSOLIDATED BASIS OR ON BASES
                        INVOLVING CLAIMS BROUGHT IN A PURPORTED REPRESENTATIVE CAPACITY ON BEHALF OF THE GENERAL PUBLIC,
                        OTHER USERS OF THE SERVICES, OR ANY OTHER PERSONS. THE ARBITRATOR MAY AWARD RELIEF ONLY IN FAVOR
                        OF THE INDIVIDUAL PARTY SEEKING RELIEF AND ONLY TO THE EXTENT NECESSARY TO PROVIDE RELIEF
                        WARRANTED BY THAT INDIVIDUAL PARTY’S CLAIM. THE ARBITRATOR MAY NOT AWARD RELIEF FOR OR AGAINST
                        ANYONE WHO IS NOT A PARTY. THE ARBITRATOR MAY NOT CONSOLIDATE MORE THAN ONE PERSON’S CLAIMS, AND
                        MAY NOT OTHERWISE PRESIDE OVER ANY FORM OF A REPRESENTATIVE OR CLASS PROCEEDING. THIS WAIVER OF
                        CLASS ACTIONS AND COLLECTIVE RELIEF IS AN ESSENTIAL PART OF THIS ARBITRATION PROVISION AND CANNOT
                        BE SEVERED FROM IT.
                    </strong>
                </p>
                <p>
                    <strong>
                        BY AGREEING TO THE ARBITRATION OF DISPUTES AS SET FORTH HEREIN, YOU AGREE THAT YOU ARE WAIVING
                        YOUR RIGHT TO A JURY TRIAL AND LIMITING YOUR RIGHT TO APPEAL, AND YOU UNDERSTAND THAT YOU ARE
                        WAIVING YOUR RIGHTS TO OTHER AVAILABLE RESOLUTION PROCESSES, SUCH AS A COURT ACTION.
                    </strong>
                </p>
                <p><strong>THE ARBITRATOR HAS NO AUTHORITY TO AWARD PUNITIVE DAMAGES.</strong></p>
                <p>
                    15.6. If any part or parts of this Arbitration Agreement are found under the law to be invalid or
                    unenforceable, then such specific part or parts shall be of no force and effect and shall be
                    severed, and the remainder of the Arbitration Agreement shall continue in full force and effect.
                </p>
                <p>
                    15.7. This Arbitration Agreement shall survive the termination of your relationship with the
                    Company.
                </p>
            </section>

            <section>
                <h2>16. Termination and Assignment</h2>
                <p>
                    16.1. These TOS and/or any other policies will continue in effect until terminated by either you or
                    us.
                </p>
                <p>
                    16.2. You may terminate these TOS at any time by ceasing your access and use of the Services and, as
                    applicable, cancelling your paid subscription.
                </p>
                <p>
                    16.3. We reserve the right, at our sole discretion, to terminate or suspend your access to the
                    Services, your account, or any portion thereof without prior notice, for any reason, including but
                    not limited to:
                </p>
                <p>(i) violation of these TOS or any other policies governing the use of the Services;</p>
                <p>(ii) engagement in fraudulent, illegal, or harmful activities; or</p>
                <p>
                    (iii) any conduct that, in our sole discretion, may harm the Company, our users, or third parties.
                </p>
                <p>
                    In the event of termination by us, we will make reasonable efforts to notify you via the email
                    address associated with your account.
                </p>
                <p>16.4. Upon termination of your account, whether by you or by Company:</p>
                <p>
                    (i) you will no longer have access to your account or any data, content, or information stored on
                    the Services; and
                </p>
                <p>(ii) any licenses or rights granted to you under these TOS will immediately cease.</p>
                <p>
                    The foregoing is subject to your data-protection rights and our data-retention obligations as set
                    out in Section 19. Termination does not extinguish any right you have to request access to, or
                    erasure of, your personal data, and we may retain certain data where required to meet a legal
                    obligation or to establish, exercise, or defend legal claims.
                </p>
                <p>
                    16.5. We reserve and retain the rights to assign, transfer or subcontract the Services to any third
                    parties. Notice will be posted on the Services and your continued use or update of the Services
                    indicates your consent.
                </p>
            </section>

            <section>
                <h2>17. Miscellaneous</h2>
                <p>
                    17.1. If any provision of these TOS or any other policies is found to be unlawful, void, or
                    unenforceable for any reason, that provision will not affect the validity and enforceability of any
                    remaining provision, and such provision will be enforced to the maximum extent possible so as to
                    affect the intent of the parties.
                </p>
                <p>
                    17.2. These TOS and other Policies constitute your entire agreement with us pertaining to your
                    access and use of the Services. Any prior or contemporaneous written or oral agreements pertaining
                    thereto are hereby superseded.
                </p>
                <p>
                    17.3. We collect and process your personal data in accordance with Section 19 (Privacy and Data
                    Protection) of these TOS, which forms an integral part of this agreement.
                </p>
                <p>
                    17.4. All information communicated on the Services is considered electronic communication. When you
                    communicate with us through or on the Service or via other forms of electronic media, such as
                    e-mail, you are communicating with us electronically. You agree that we may communicate
                    electronically with you and that such communications, as well as notices, disclosures, agreements,
                    and other communications that we provide to you electronically, are equivalent to communications in
                    writing and shall have the same force and effect as if they were in writing and signed by the party
                    sending the communication. You also agree that such communication may be conducted by using
                    third-party providers that allow us to manage and facilitate these electronic interactions
                    efficiently and securely. In recognition of the diverse and complex nature of our Service, you
                    acknowledge that we engage third-party providers for a broad range of services that support and
                    enhance our offerings. This may include, but is not limited to, processing transactions, as well as
                    other operational, technical, and logistical support functions. Our use of third-party service
                    providers allows us to deliver our Service more efficiently and effectively for you. You further
                    acknowledge and agree that by clicking on a button labeled “SUBMIT”, “CONTINUE”, “REGISTER”, “I
                    AGREE” or similar links or buttons, you are submitting a legally binding electronic signature and
                    are entering into a legally binding contract. You acknowledge that your electronic submissions
                    constitute your agreement and intent to be bound by these Terms. YOU HEREBY AGREE TO THE USE OF
                    ELECTRONIC SIGNATURES, CONTRACTS, ORDERS, AND OTHER RECORDS AND TO THE ELECTRONIC DELIVERY OF
                    NOTICES, POLICIES, AND RECORDS OF TRANSACTIONS INITIATED OR COMPLETED THROUGH THE SERVICE.
                </p>
            </section>

            <section>
                <h2>18. Contact Information</h2>
                <p><a href="mailto:complains@hotmate.ai">complains@hotmate.ai</a></p>
            </section>

            <section>
                <h2>19. Privacy and Data Protection</h2>
                <p>
                    This Section explains how we collect, use, and protect your personal data and constitutes our
                    privacy notice for the purposes of the EU General Data Protection Regulation (Regulation (EU)
                    2016/679, “GDPR”) and applicable data protection law. It forms part of these TOS.
                </p>
                <p>
                    19.1. Data controller. The controller of your personal data is Hotmate Ltd, a company incorporated
                    in Cyprus (registration number HE 475961) (the “Company”, “we”, “us”). For any data protection
                    matter, including to exercise your rights, you may contact us at privacy@hotmate.ai (or
                    complains@hotmate.ai).
                </p>
                <p>
                    19.2. Personal data we collect. We collect: (i) account and login data (such as your email address
                    and password or other login identifiers); (ii) payment data processed through our third-party
                    payment processor (we do not store full card numbers); (iii) the Content you submit and your
                    conversations with AI Companions; and (iv) technical and usage data (such as device identifiers, IP
                    address, log data, and information collected through cookies and similar technologies).
                </p>
                <p>
                    19.3. Special category data. The Services may involve adult-oriented conversations and Content.
                    Depending on how you use the Services, your Content may reveal information concerning your sex life
                    or sexual orientation, which is special category data under Article 9 GDPR. Where we process such
                    data, we do so only where necessary to provide the Services requested by you and on the basis of
                    your explicit consent. You provide this consent by actively confirming, before using the relevant
                    adult chat features, that you agree to the processing of such Content for the purpose of providing,
                    moderating, securing, and maintaining the Services. You may withdraw this consent at any time by
                    contacting us at privacy@hotmate.ai or by deleting your account. Withdrawal does not affect the
                    lawfulness of processing carried out before withdrawal.
                </p>
                <p>
                    19.4. Purposes and lawful bases. We process your personal data: (i) to provide, maintain, and
                    operate the Services and perform our contract with you (Article 6(1)(b) GDPR); (ii) to comply with
                    our legal obligations, including age verification and responding to lawful requests (Article
                    6(1)(c)); (iii) for our legitimate interests in keeping the Services safe and secure, content
                    moderation, preventing fraud and abuse, and improving the Services (Article 6(1)(f)); and (iv) on
                    the basis of your consent, including for any marketing communications and, as set out in clause
                    19.3, for special category data (Articles 6(1)(a) and 9(2)(a)).
                </p>
                <p>
                    19.5. Recipients. We share personal data with service providers acting as our processors on our
                    instructions, including payment processors, hosting and infrastructure providers, and
                    content-moderation and artificial-intelligence service providers. We may also disclose personal data
                    where required by law or to protect our rights, users, or the public.
                </p>
                <p>
                    19.6. International transfers. Where we transfer personal data outside the European Economic Area, we
                    ensure an adequate level of protection through an appropriate safeguard recognised under the GDPR,
                    such as an adequacy decision or the European Commission’s Standard Contractual Clauses. You may
                    request a copy of the relevant safeguard using the contact details in clause 19.1.
                </p>
                <p>
                    19.7. Retention. We retain personal data only for as long as necessary for the purposes described in
                    this Section, and as required by applicable law. The criteria we use to determine retention periods
                    include the duration of your account and use of the Services, our legal, tax, and accounting
                    obligations, and the need to prevent abuse and to establish, exercise, or defend legal claims. When
                    personal data is no longer required, we delete or anonymise it.
                </p>
                <p>
                    19.8. Your rights. Subject to the conditions and exceptions in the GDPR, you have the right to:
                    access your personal data; have inaccurate data rectified; have your data erased; restrict or object
                    to processing; data portability; and withdraw consent at any time. To exercise any of these rights,
                    contact us using the details in clause 19.1. We will respond within the time limits required by law.
                </p>
                <p>
                    19.9. Right to lodge a complaint. You have the right to lodge a complaint with a data protection
                    supervisory authority, in particular the Office of the Commissioner for Personal Data Protection of
                    Cyprus (www.dataprotection.gov.cy) or the supervisory authority in your country of residence or
                    workplace. Nothing in these TOS limits this right or your right to a judicial remedy under the GDPR.
                </p>
                <p>
                    19.10. Automated moderation. We use automated tools to detect content that may violate these TOS,
                    protect users, prevent fraud and abuse, and maintain the safety of the Services. Automated tools may
                    flag Content for review or restrict obviously prohibited activity. Decisions that may significantly
                    affect your account, such as account termination or reporting to authorities, are not made solely by
                    automated means and are subject to human review where required by applicable law. You may contact us
                    at privacy@hotmate.ai to contest such a decision or express your point of view.
                </p>
                <p>
                    19.11. Security and changes. We implement appropriate technical and organisational measures to
                    protect your personal data against unauthorised access, loss, or misuse. We may update this Section
                    from time to time; material changes will be notified in accordance with the change-notification
                    provisions of these TOS.
                </p>
            </section>
        </LegalLayout>
    );
}

export function Privacy() {
    return (
        <LegalLayout title="Privacy Policy" eyebrow="Legal">
            <section>
                <p className="legalIntro">
                    This Privacy Policy explains what information {SITE.name} collects, how we use it, and the choices you have.
                    We take your privacy seriously — especially given the nature of the content our service produces.
                </p>
            </section>

            <section>
                <h2>1. Information We Collect</h2>
                <p>
                    <strong>We do not accept user-uploaded photos or videos.</strong> The {SITE.name} platform has no upload
                    functionality — you cannot send us a real photograph. All imagery available through the service is
                    generated by our AI from parameters you select. We therefore never collect, process, or store any
                    user-supplied photographs or biometric data.
                </p>
                <p><strong>Account data.</strong> Email address (via direct sign-up or Google OAuth), authentication tokens,
                account creation timestamp.</p>
                <p><strong>Usage data.</strong> Generation parameters (pose, outfit, etc.), generated media URLs linked to your
                account, credit balance, and a log of generation requests.</p>
                <p><strong>Payment data.</strong> Handled entirely by our payment processor (Finby). We store only a transaction
                reference and the purchased tier — never card numbers, CVVs, or billing addresses.</p>
                <p><strong>Technical data.</strong> IP address, user agent, browser type, and basic device information for
                fraud prevention and debugging.</p>
            </section>

            <section>
                <h2>2. How We Use Your Information</h2>
                <ul>
                    <li>To operate the service (authenticate, process generations, deliver results)</li>
                    <li>To process payments and allocate credits</li>
                    <li>To prevent fraud, abuse, and misuse</li>
                    <li>To improve the quality of our AI models (in aggregated, non-identifiable form)</li>
                    <li>To communicate service-related updates</li>
                </ul>
            </section>

            <section>
                <h2>3. Third-Party Services</h2>
                <p>We share limited data with third parties strictly to operate the service:</p>
                <ul>
                    <li><strong>Finby</strong> — payment processing</li>
                    <li><strong>Google OAuth</strong> — optional sign-in (name and email only)</li>
                    <li><strong>Cloud infrastructure</strong> (DigitalOcean Spaces) — media storage</li>
                    <li><strong>GPU providers</strong> (RunPod or equivalent) — running generation models</li>
                </ul>
                <p>We do not sell your personal data to third parties. Ever.</p>
            </section>

            <section>
                <h2>4. Generated Content</h2>
                <p>
                    Your generated media is stored on encrypted cloud storage and accessible only via tokenized URLs.
                    We do not share your gallery with other users. Generations are kept as long as your account is active;
                    when you delete your account, generations are deleted within 30 days.
                </p>
            </section>

            <section>
                <h2>5. Cookies & Local Storage</h2>
                <p>
                    We use local storage to keep you signed in (authentication token) and to track small UX state
                    (like the tap-game set rotator). We do not use advertising cookies or third-party tracking pixels.
                </p>
            </section>

            <section>
                <h2>6. Data Retention</h2>
                <p>
                    Account data is retained for as long as your account is active. Upon deletion request, we remove personal data
                    within 30 days, except where retention is required by law (e.g. payment records for tax purposes).
                </p>
            </section>

            <section>
                <h2>7. Your Rights</h2>
                <p>Depending on your jurisdiction (GDPR, CCPA, etc.), you may have the right to:</p>
                <ul>
                    <li>Access a copy of your personal data</li>
                    <li>Request correction or deletion</li>
                    <li>Object to certain processing</li>
                    <li>Data portability</li>
                </ul>
                <p>
                    To exercise these rights, email us at <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
                    We respond within 30 days.
                </p>
            </section>

            <section>
                <h2>8. Security</h2>
                <p>
                    We use TLS for all network traffic, hashed and salted password storage, and industry-standard access controls.
                    No system is 100% secure — if we ever experience a breach affecting you, we'll notify you promptly.
                </p>
            </section>

            <section>
                <h2>9. Children</h2>
                <p>
                    {SITE.name} is strictly for adults. We do not knowingly collect information from anyone under 18. If you
                    believe a minor has created an account, email us immediately and we will remove it.
                </p>
            </section>

            <section>
                <h2>10. Changes to This Policy</h2>
                <p>
                    We'll update the "Last updated" date when this policy changes. For material changes, we'll notify signed-in
                    users by email.
                </p>
            </section>

            <section>
                <h2>11. Contact</h2>
                <p>
                    Privacy questions? Email <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
                </p>
            </section>
        </LegalLayout>
    );
}

export function Compliance2257() {
    return (
        <LegalLayout title="18 U.S.C. § 2257 Compliance Statement" eyebrow="Compliance">
            <section>
                <p className="legalIntro">
                    This statement addresses the applicability of the record-keeping requirements under
                    18 U.S.C. § 2257 and 28 C.F.R. Part 75 to content distributed by {SITE.name}.
                </p>
            </section>

            <section>
                <h2>AI-Generated Content Only</h2>
                <p>
                    All visual content produced and made available by {SITE.name} is <strong>entirely generated by
                    artificial-intelligence models</strong>. No content on this platform depicts real human beings,
                    actual sexual conduct, or any actual performers.
                </p>
                <p>
                    Because 18 U.S.C. § 2257 applies to depictions of <em>actual</em> sexually explicit conduct
                    performed by <em>actual</em> human beings, and because no such persons or conduct exist in our
                    synthetic content, the record-keeping obligations under § 2257 and 28 C.F.R. Part 75 are
                    not applicable to {SITE.name}.
                </p>
            </section>

            <section>
                <h2>No Real Persons Depicted</h2>
                <p>
                    {SITE.name} strictly prohibits the generation, upload, or distribution of content that resembles,
                    impersonates, or attempts to recreate any real identifiable individual. Any content reported as
                    depicting a real person will be removed, and the responsible account will be terminated.
                </p>
            </section>

            <section>
                <h2>Prohibition on Depictions of Minors</h2>
                <p>
                    {SITE.name} maintains zero tolerance for any content that depicts or appears to depict minors in
                    any sexual context. Our models, policies, and monitoring systems work to detect and block such
                    content. Any attempt to produce such material will result in immediate account termination and
                    may be reported to the National Center for Missing & Exploited Children (NCMEC) and law enforcement.
                </p>
            </section>

            <section>
                <h2>Designated Contact</h2>
                <p>
                    Questions about content on {SITE.name}, or reports of suspected misuse, should be sent to
                    <a href={`mailto:${CONTACT_EMAIL}`}> {CONTACT_EMAIL}</a>. Reports will be reviewed within 24 hours.
                </p>
            </section>

            <section>
                <h2>Disclaimer</h2>
                <p>
                    This statement is provided for informational purposes only and does not constitute legal advice.
                    Operators and users should consult qualified legal counsel regarding applicable laws in their
                    jurisdiction.
                </p>
            </section>
        </LegalLayout>
    );
}

export function Contact() {
    return (
        <LegalLayout title="Get in touch" eyebrow="Contact">
            <section>
                <p className="legalIntro">
                    Whether you've got a question, found a bug, have a partnership idea, or need help with your account —
                    we're real humans and we read every message.
                </p>
            </section>

            <div className="contactGrid">
                <a href={`mailto:${CONTACT_EMAIL}`} className="contactCard">
                    <div className="contactCardIcon">✉</div>
                    <div className="contactCardTitle">General & Support</div>
                    <div className="contactCardValue">{CONTACT_EMAIL}</div>
                    <div className="contactCardDesc">Account issues, bug reports, feature requests. Response within 24h.</div>
                </a>

                <a href={`mailto:${SITE.emails.abuse}`} className="contactCard">
                    <div className="contactCardIcon">!</div>
                    <div className="contactCardTitle">Abuse & Takedown</div>
                    <div className="contactCardValue">{SITE.emails.abuse}</div>
                    <div className="contactCardDesc">Report content, copyright claims, impersonation. Reviewed within 24h.</div>
                </a>

                <a href={`mailto:${SITE.emails.press}`} className="contactCard">
                    <div className="contactCardIcon">◆</div>
                    <div className="contactCardTitle">Press & Partnerships</div>
                    <div className="contactCardValue">{SITE.emails.press}</div>
                    <div className="contactCardDesc">Media, investors, business collaborations.</div>
                </a>

                <a href={`mailto:${SITE.emails.legal}`} className="contactCard">
                    <div className="contactCardIcon">§</div>
                    <div className="contactCardTitle">Legal</div>
                    <div className="contactCardValue">{SITE.emails.legal}</div>
                    <div className="contactCardDesc">DMCA, subpoenas, legal notices, compliance questions.</div>
                </a>
            </div>

            <section>
                <h2>Response times</h2>
                <p>
                    Support emails are answered within 24 hours on weekdays. Abuse reports are prioritized and reviewed
                    within the same business day. Legal and press inquiries may take 2–3 business days.
                </p>
            </section>

            <section>
                <h2>Before you write</h2>
                <p>
                    For account and payment issues, please include your account email and a short description of what
                    went wrong. For content reports, include the URL or media ID if you have one.
                </p>
            </section>
        </LegalLayout>
    );
}
